/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useQueryClient} from 'react-query';
import {makeStyles} from '@material-ui/core';
import isEqual from 'lodash.isequal';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {createSFTPServer, editSFTPServer, testSFTPServer} from '@ion/core/src/services/Reports';
import {AccountLink, SFTPConnection} from '@ion/core/src/types/entities';
import {safeParseNumber} from '@ion/core/src/utils/NumberUtil';
import FormGroupFields from '@ion/dls/src/components/forms/FormGroupFields';
import QueryKey from '@ion/query/src/config/QueryKeys';
import useSFTPConfigurations from '@ion/query/src/hooks/useSFTPConfigurations';
import {FormItem} from '../components/forms/FormItem';
import {FormNumberField} from '../components/forms/FormNumberField';
import {FormSection} from '../components/forms/FormSection';
import {FormTextField} from '../components/forms/FormTextField';
import {useFormSave} from '../hooks/useFormSave';
import {useFormState} from '../hooks/useFormState';
import {IonSnackbar} from '../notifications/IonSnackbar';
import {toggleThemeStyle} from '../theme/helpers';
import {CancelButton} from './controls/CancelButton';
import {SaveButton} from './controls/SaveButton';
import AccountsChooser from './AccountsChooser';
import SFTPConfigurationChooser from './SFTPConfigurationChooser';
import {deserialize, serializer, SFTPConfigurationFields} from './SFTPConfigurationEditorUtils';

export interface SFTPEditorEnablement {
  includeSFTPEditor?: boolean;
};

export interface SFTPConfigurationProps {
  account: AccountLink;
  isView?: boolean;
  v2?: boolean;
};

/**
 * This interface defines the props for an SFTP Configuration Editor component.
 * It includes the associated account (`account`), an optional callback for handling
 * changes (`onChange`), a default value (`defaultValue`), and a flag to determine
 * whether to include a form (`includeForm`).
 */
export interface SFTPConfigurationEditorProps extends SFTPConfigurationProps{
  onChange?: Function;
  defaultValue?: string;
  includeForm?: boolean;
  showSSOConfig?: boolean;
  isCurrentTheme?: boolean;
};

/**
 * This interface defines the props for an SFTP Configuration Form component.
 * It includes the SFTP connection configuration to be displayed (`configuration`)
 * and the associated account (`account`).
 */
export interface SFTPConfigurationFormProps extends SFTPConfigurationProps{
  configuration: SFTPConnection;
  omitSubAccountsControl?: boolean;
  showSSOConfig?: boolean;
};

// defines the style defintion for SFTPConfigurationForm component ...
const useFormStyle = makeStyles(() => ({
  formSection : {
    paddingTop : '0px !important'
  },
  // pushes the "Edit" CTA to the right of the "SFTP Server" title, matching
  // the legacy theme's spacing; only the current theme lacked it ...
  formSectionContent : {
    justifyContent : toggleThemeStyle(undefined, 'space-between')
  },
  root : {
    display       : 'flex',
    flexDirection : 'column'
  },
  actions : {
    display    : 'flex',
    alignSelf  : 'flex-end',
    alignItems : 'center'
  },
  saveButtonContainer : {
    margin : '0px !important',
    width  : '122px'
  },
  testButtonContainer : {
    marginRight : '5px !important',
    width       : '122px'
  }
}));

/**
 * A component for displaying and editing SFTP connection configurations.
 *
 * ```typescript
 *
 *  import {SFTPConfigurationForm} from '@ion/dls/src/editors/';
 *
 *   <SFTPConfigurationForm account={account} configuration={configuration} />
 *
 * ```
 * @param configuration - The initial SFTP connection configuration.
 * @param account - The associated account link for the configuration.
 */
export const SFTPConfigurationForm = ({
  configuration,
  account,
  isView = false,
  v2 = false,
  showSSOConfig = false,
  omitSubAccountsControl = true
}: SFTPConfigurationFormProps) => {
  const queryClient                   = useQueryClient();
  const classes                       = useFormStyle();
  const [disableTest, setDisableTest] = useState(true);
  const [disableSave, setDisableSave] = useState(true);
  const [isEdit, setIsEdit]           = useState(false);
  const [disabled, setDisabled]       = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [message, setMessage]         = useState(null);
  const [formState, setFormState]     = useFormState(deserialize(configuration || {} as SFTPConnection));
  const isDisabled                    = isView || disabled;
  const accountId                     = useMemo(() => entityId(account), [account]);
  const {configurations}              = useSFTPConfigurations({accountId, enabled: !!accountId, v2});

  const refreshSFTPConfigurations = useCallback(() => {
    queryClient.invalidateQueries([QueryKey.GET_SFTP_CONFIGURATIONS, accountId, v2]);
  }, [accountId, queryClient, v2]);

  const onCancel = useCallback((resetForm = true) => {
    setDisabled(!!configuration);
    setIsEdit(false);
    setDisableTest(true);
    if (resetForm) {
      setFormState(deserialize(configuration || {} as SFTPConnection));
    }
  }, [configuration]);

  const onTest = useCallback(async(config) => {
    try {
      setMessage(null);
      await testSFTPServer(config);
      setMessage(translate('admin-groups:sftpEditor.form.edit.test'));
      setDisableSave(false);
    } catch (e) {
      setMessage(translate('admin-groups:sftpEditor.form.edit.error'));
      setDisableSave(true);
    }
  }, [configuration]);

  const onSubmit = useCallback(async(config) => {
    try {
      const match = configurations.find(item =>
        item?.name.toLowerCase() === config?.name.toLowerCase()
      );
      setMessage(null);
      setSaveLoading(true);
      if (v2) {
        isEdit || match ? await editSFTPServer(configuration?.id || match?.id, config) : await createSFTPServer(config);
      } else {
        await createSFTPServer(accountId, config);
      }
      refreshSFTPConfigurations();
      onCancel(config?.[SFTPConfigurationFields.Name] !== configuration?.[SFTPConfigurationFields.Name]);
      setMessage(translate('admin-groups:sftpEditor.form.edit.success'));
    } catch (e) {
      setMessage(translate('admin-groups:sftpEditor.form.edit.error'));
    } finally {
      setSaveLoading(false);
    }
  }, [accountId, configurations, isEdit, refreshSFTPConfigurations, configuration, onCancel, v2]);

  const submitValidation = useMemo(() => ({
    [SFTPConfigurationFields.Password] : ({value}) => {
      return formState[SFTPConfigurationFields.RSAPrivateKey].value || value;
    },
    [SFTPConfigurationFields.RSAPrivateKey] : ({value}) => {
      return formState[SFTPConfigurationFields.Password].value || value;
    }
  }), [formState]);

  const isActiveForm = useMemo(() => (
    configuration ? !disabled : !disableTest
  ), [configuration, disabled, disableTest]);

  const handleSubmit = useFormSave({
    validate  : submitValidation,
    serialize : serializer,
    onSave    : onSubmit,
    formState,
    setFormState
  });

  const handleTest = useFormSave({
    validate  : submitValidation,
    serialize : serializer,
    onSave    : onTest,
    formState,
    setFormState
  });

  useEffect(() => {
    setFormState(deserialize(configuration || {} as SFTPConnection));
    setDisabled(!!configuration);
  }, [configuration]);

  useEffect(() => {
    const configValues = Object.fromEntries(
      Object.entries(deserialize(configuration || {} as SFTPConnection)).map(([key, val]) => [key, val?.value])
    );
    const formValues = Object.fromEntries(
      Object.entries(formState).map(([key, val]) => [key, val?.value])
    );
    setDisableTest(isEqual(configValues, formValues));
  }, [formState, configuration]);

  const onFieldChange = useCallback(({target: {value, name}}) => {
    const serilizedValue = name === SFTPConfigurationFields.Port ? safeParseNumber(value, '') : value;
    setFormState({
      [name] : {
        value    : serilizedValue,
        error    : false,
        required : formState[name].required
      },
      ...(name === SFTPConfigurationFields.Password && ({
        [SFTPConfigurationFields.RSAPrivateKey] : {
          ...formState[SFTPConfigurationFields.RSAPrivateKey],
          error : false
        }
      })),
      ...(name === SFTPConfigurationFields.RSAPrivateKey && ({
        [SFTPConfigurationFields.Password] : {
          ...formState[SFTPConfigurationFields.Password],
          error : false
        }
      }))
    });
  }, [formState]);

  const onSubAccountChange = useCallback((value) => {
    setFormState({
      [SFTPConfigurationFields.AllowedAccounts] : {
        error : false,
        value
      }
    });
  }, []);

  const onEditCTAClick = useCallback(() => {
    setIsEdit(true);
    setDisableSave(true);
    setDisabled(false);
  }, []);

  // unset the message from state when the snakbar is closed...
  const onMessageClose = useCallback(function() {
    setMessage(null);
  }, []);

  return (
    <div className={classes.root}>
      <FormSection title={translate('admin-groups:sftpEditor.form.title')}
        overrideClasses={{formSection: classes.formSection, formSectionContent: classes.formSectionContent}}
        bottomBorder={showSSOConfig}
        ctaText={disabled && configuration && !isView && translate('admin-groups:sftpEditor.form.ctaText')}
        onCTAClick={onEditCTAClick}>
        <FormGroupFields>
          <FormItem>
            <FormTextField fieldLabel={translate('admin-groups:sftpEditor.form.serverName.fieldLabel')}
              helperText={!disabled && translate('admin-groups:sftpEditor.form.serverName.helperText')}
              value={formState[SFTPConfigurationFields.Name].value}
              hasError={formState[SFTPConfigurationFields.Name].error}
              errorText={translate('common:editors.shared.required')}
              name={SFTPConfigurationFields.Name}
              onChange={onFieldChange}
              required={isActiveForm}
              disabled={isDisabled} />
          </FormItem>
          <FormItem>
            <FormTextField fieldLabel={translate('admin-groups:sftpEditor.form.host.fieldLabel')}
              value={formState[SFTPConfigurationFields.Host].value}
              hasError={formState[SFTPConfigurationFields.Host].error}
              errorText={translate('common:editors.shared.required')}
              name={SFTPConfigurationFields.Host}
              onChange={onFieldChange}
              required={isActiveForm}
              disabled={isDisabled} />
          </FormItem>
          <FormItem>
            <FormNumberField fieldLabel={translate('admin-groups:sftpEditor.form.port.fieldLabel')}
              value={formState[SFTPConfigurationFields.Port].value}
              hasError={formState[SFTPConfigurationFields.Port].error}
              errorText={translate('common:editors.shared.required')}
              name={SFTPConfigurationFields.Port}
              onChange={onFieldChange}
              thousandSeparator={false}
              required={isActiveForm}
              disabled={isDisabled}
              max={65535}
              min={1}
              allowedRange />
          </FormItem>
          <FormItem>
            <FormTextField name={SFTPConfigurationFields.Username}
              fieldLabel={translate('admin-groups:sftpEditor.form.username.fieldLabel')}
              value={formState[SFTPConfigurationFields.Username].value}
              hasError={formState[SFTPConfigurationFields.Username].error}
              errorText={translate('common:editors.shared.required')}
              required={isActiveForm}
              onChange={onFieldChange}
              disabled={isDisabled} />
          </FormItem>
          <FormItem>
            <FormTextField fieldLabel={translate('admin-groups:sftpEditor.form.password.fieldLabel')}
              value={formState[SFTPConfigurationFields.Password].value}
              hasError={formState[SFTPConfigurationFields.Password].error}
              errorText={translate('admin-groups:sftpEditor.form.password.errorText')}
              name={SFTPConfigurationFields.Password}
              onChange={onFieldChange}
              disabled={isDisabled} />
          </FormItem>
          <FormItem>
            <FormTextField fieldLabel={translate('admin-groups:sftpEditor.form.privateKey.fieldLabel')}
              value={formState[SFTPConfigurationFields.RSAPrivateKey].value}
              hasError={formState[SFTPConfigurationFields.RSAPrivateKey].error}
              errorText={translate('admin-groups:sftpEditor.form.privateKey.errorText')}
              name={SFTPConfigurationFields.RSAPrivateKey}
              onChange={onFieldChange}
              disabled={isDisabled} />
          </FormItem>
          {!omitSubAccountsControl && (
            <FormItem>
              <AccountsChooser account={account}
                value={formState[SFTPConfigurationFields.AllowedAccounts].value}
                defaultValue={formState[SFTPConfigurationFields.AllowedAccounts].value}
                hasError={formState[SFTPConfigurationFields.AllowedAccounts].error}
                onChange={onSubAccountChange}
                disabled={isDisabled} />
            </FormItem>
          )}
        </FormGroupFields>
      </FormSection>
      {isActiveForm && (
        <div className={classes.actions}>
          <CancelButton onCancel={onCancel} disabled={saveLoading} />
          <SaveButton label={translate('common:editors.shared.test')}
            disabled={disableTest}
            overrideClasses={{
              container : classes.testButtonContainer
            }}
            onSave={handleTest} />
          <SaveButton label={translate('common:editors.shared.save')}
            disabled={disableSave || saveLoading}
            loading={saveLoading}
            overrideClasses={{
              container : classes.saveButtonContainer
            }}
            onSave={handleSubmit} />
        </div>
      )}
      {message && (
        <IonSnackbar message={message}
          actionLabel={translate('common:ok')}
          autoHideDuration={3000}
          onAction={onMessageClose}
          onClose={onMessageClose}
          show/>
      )}
    </div>
  );
};

/**
 * A component for selecting and editing SFTP connection configurations from any editor.
 *
 * ```typescript
 *
 *  import {SFTPConfigurationEditor} from '@ion/dls/src/editors/';
 *
 *   <SFTPConfigurationEditor account={account} onChange={onChange}
 *     defaultValue={defaultValue} />
 *
 * ```
 *
 * @param account - The associated account link for the configuration.
 * @param onChange - A callback function triggered when the configuration changes.
 * @param defaultValue - The default value to be selected initially.
 * @param includeForm - Determines whether to include the configuration form.
 */
export const SFTPConfigurationEditor = ({
  account,
  onChange,
  defaultValue,
  isView = false,
  v2 = false,
  includeForm = true,
  isCurrentTheme = false,
  showSSOConfig = false
}: SFTPConfigurationEditorProps) => {
  const [selected, setSelected] = useState<any>(defaultValue);

  const accountId = useMemo(() => entityId(account), [account]);

  const onConfigurationChange = useCallback((configuration) => {
    setSelected(configuration);
    onChange?.(configuration);
  }, [onChange]);

  return (
    <>
      <FormSection title={translate('admin-groups:sftpEditor.editor.title')}
        description={translate('admin-groups:sftpEditor.editor.description')} bottomBorder={showSSOConfig}>
        <FormGroupFields>
          <FormItem fullWidth>
            <SFTPConfigurationChooser accountId={accountId}
              onChange={onConfigurationChange}
              isView={isView}
              v2={v2}
              value={selected}
              account={account}
              isCurrentTheme={isCurrentTheme}
              defaultValue={defaultValue} />
          </FormItem>
        </FormGroupFields>
      </FormSection>
      {includeForm && (
        <SFTPConfigurationForm configuration={selected}
          showSSOConfig={showSSOConfig}
          isView={isView}
          v2={v2}
          account={account} />
      )}
    </>
  );
};

export default SFTPConfigurationEditor;
